"use client";

import { useState, useEffect, useRef } from "react";
import {
  Camera,
  ImageIcon,
  MapPin,
  Link2,
  Hash,
  X,
  Loader2,
  AlertCircle,
  Plus,
  Search,
  Check,
  RefreshCw,
} from "lucide-react";
import Button from "./Button";
import MobileHeader from "./MobileHeader";
import { classNames } from "../../utils/classNames";
import ImagePickerModal from "./ImagePickerModal";
import { getScoops, getHoops, postScoops } from "../../services/scoops";
import { handlegetAllGroup, handleAddGroupPost } from "../../services/group";
import { successToast, errorToast, warningToast } from '../../utils/toasters'
import { jwtDecode } from "jwt-decode";
import { isAuthenticated } from "../../utils/helpers";
import { useNavigate } from "react-router-dom";

function Hashtag({ tag, onClick, selected }) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "px-3 py-1 rounded-full text-sm font-medium",
        selected
          ? "bg-black text-white"
          : "border text-black hover:bg-gray-800 hover:text-white"
      )}
    >
      {tag}
    </button>
  );
}

function ImagePreview({ src, onRemove }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className="relative rounded-md overflow-hidden border border-gray-200 h-32 w-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 p-2">
          <AlertCircle className="h-6 w-6 text-red-500 mb-1" />
          <p className="text-xs text-gray-600 text-center">Failed to load</p>
        </div>
      )}

      <img
        src={src || "/placeholder.svg"}
        alt="Uploaded content"
        className={classNames(
          "w-full h-full object-cover",
          isLoading || hasError ? "opacity-0" : "opacity-100"
        )}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* Improved close button with better positioning and visibility */}
      <button
        className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70 transition-colors"
        onClick={onRemove}
      >
        <X size={16} />
      </button>
    </div>
  );
}

function MobileCreateScoop({ onClose, onPost, isProcessing = false }) {
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedHashtags, setSelectedHashtags] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [images, setImages] = useState([]);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [imagePickerMode, setImagePickerMode] = useState("upload");
  const [step, setStep] = useState("category");
  const [showMaxImagesWarning, setShowMaxImagesWarning] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const mentionGuideRef = useRef(null);

  // Mention feature states
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionType, setMentionType] = useState(null); // '@', '#', or '+'
  const [mentionText, setMentionText] = useState("");
  const [mentionOptions, setMentionOptions] = useState([]);
  const [showMentionGuide, setShowMentionGuide] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  const MAX_IMAGES = 2;
  const [geohash, setGeohash] = useState("9v6m"); // Default geohash
  const [showGroupHint, setShowGroupHint] = useState(true);

  // State for API data
  const [scoops, setScoops] = useState([]);
  const [hoops, setHoops] = useState([]);
  const [groups, setGroups] = useState([]);
  const [defaultGallyHoop, setDefaultGallyHoop] = useState(null);
  const [availableHoops, setAvailableHoops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupsError, setGroupsError] = useState(null);
  const [groupsLastFetched, setGroupsLastFetched] = useState(null);

  // Helper function to group hoops by category
  const groupHoopsByCategory = (hoopsData) => {
    // Create groups of 6 hoops each
    const groups = [];
    for (let i = 0; i < hoopsData.length; i += 6) {
      groups.push(hoopsData.slice(i, i + 6));
    }
    return groups;
  };

  // Fetch scoops and hoops when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch scoops and hoops in parallel
        const [scoopsData, hoopsData] = await Promise.all([
          getScoops(),
          getHoops(),
        ]);

        setScoops(scoopsData);
        setHoops(hoopsData);

        // Group hoops for display
        const groupedHoops = groupHoopsByCategory(hoopsData);
        setAvailableHoops(groupedHoops);

        // Find Gally Talks scoop and set default hoop
        const gallyTalksScoop = scoopsData.find(
          (scoop) =>
            scoop.scoopname === "Gally Talks" ||
            scoop.scoopname === "Galli Talks"
        );

        if (
          gallyTalksScoop &&
          gallyTalksScoop.hoops &&
          gallyTalksScoop.hoops.length > 0
        ) {
          // Find the first hoop for Gally Talks and set as default
          const firstHoopId = gallyTalksScoop.hoops[0].hoopid;
          const defaultHoop = hoopsData.find(
            (hoop) => hoop.hoopid === firstHoopId
          );
          if (defaultHoop) {
            setDefaultGallyHoop(defaultHoop);
          }
        }

        setError(null);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load categories and hashtags. Please try again.");

        errorToast("Failed to load categories and hashtags: " + err.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Also fetch groups if user is authenticated
    if (isAuthenticated()) {
      fetchGroups();
    }
  }, []);

  // Fetch groups
  const fetchGroups = async (showToast = false) => {
    try {
      setGroupsLoading(true);
      setGroupsError(null);

      const token = localStorage.getItem("access_token");
      if (!token) {
        setGroups([]);
        return [];
      }

      let userId = null;
      try {
        const decoded = jwtDecode(token);
        userId = decoded?.userid || decoded?.id || null;
      } catch (e) {
        console.error("Error decoding token:", e);
        if (showToast) {
          errorToast("Authentication error. Please log in again.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
        return [];
      }

      console.log(
        "Fetching groups with userId:",
        userId,
        "and geohash:",
        geohash
      );

      const groupsData = await handlegetAllGroup({ geoHash: geohash, userId });

      // Check if the response has the expected structure
      if (
        !groupsData ||
        !groupsData.data ||
        !Array.isArray(groupsData.data.groups)
      ) {
        console.error("Unexpected groups data format:", groupsData);
        setGroupsError("Received invalid groups data format");
        if (showToast) {
          errorToast("Failed to load groups: Invalid data format", {
            position: "top-right",
            autoClose: 5000,
          });
        }
        return [];
      }

      // Get all groups first
      const allGroups = groupsData.data.groups || [];

      // Then filter to only include groups where isJoined is true
      // Some APIs might use different property names, so we check multiple possibilities
      const joinedGroups = allGroups.filter((group) => {
        const isJoined =
          group.isJoined === true ||
          group.is_joined === true ||
          group.joined === true ||
          group.isMember === true ||
          group.is_member === true;

        // Log each group's join status for debugging
        console.log(
          `Group ${group.name || group.groupid}: isJoined=${isJoined}`,
          group
        );

        return isJoined;
      });


      // If we're showing a toast and we found groups, show a success message
      if (showToast && joinedGroups.length > 0) {
        successToast(`Found ${joinedGroups.length} groups you've joined`, {
          position: "top-right",
          autoClose: 3000,
        });
      } else if (showToast) {
        successToast("You haven't joined any groups yet", {
          position: "top-right",
          autoClose: 3000,
        });
      }

      setGroups(joinedGroups);
      setGroupsLastFetched(new Date());
      return joinedGroups;
    } catch (error) {
      console.error("Failed to load groups:", error);
      setGroupsError(error.message || "Failed to load groups");

      if (showToast) {
        errorToast(
          "Failed to load groups: " + (error.message || "Unknown error"),
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
          }
        );
      }
      return [];
    } finally {
      setGroupsLoading(false);
    }
  };

  // Get geolocation when component mounts
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // In a real app, you would convert lat/long to geohash
          // For now, we'll use a default value
          setGeohash("9v6m");
        },
        (error) => {
          console.error("Error getting geolocation:", error);
        }
      );
    }
  }, []);

  // Hide max images warning after 3 seconds
  useEffect(() => {
    if (showMaxImagesWarning) {
      const timer = setTimeout(() => {
        setShowMaxImagesWarning(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showMaxImagesWarning]);

  // Update available hashtags when category changes
  useEffect(() => {
    if (selectedCategory && scoops.length > 0) {
      // Find the selected scoop
      const scoop = scoops.find((s) => s.scoopid === selectedCategory);

      if (scoop && scoop.hoops) {
        // Group the hoops for this scoop
        const scoopHoops = scoop.hoops.map((h) => h.hoopid);

        // Filter all hoops to only include those for this scoop
        const filteredHoops = hoops.filter((h) =>
          scoopHoops.includes(h.hoopid)
        );

        // Group the filtered hoops
        const groupedHoops = groupHoopsByCategory(filteredHoops);
        setAvailableHoops(groupedHoops);
      }
    }
  }, [selectedCategory, scoops, hoops]);

  // Set default scoop to "Gally Talks" when a group is selected
  useEffect(() => {
    if (selectedGroups.length > 0) {
      // Find the Gally Talks scoop
      const gallyTalksScoop = scoops.find(
        (scoop) =>
          scoop.scoopname === "Gally Talks" || scoop.scoopname === "Galli Talks"
      );

      if (gallyTalksScoop) {
        setSelectedCategory(gallyTalksScoop.scoopid);

        // Fetch hoops for this scoop
        const scoopHoops = gallyTalksScoop.hoops || [];
        const filteredHoops = hoops.filter((h) =>
          scoopHoops.some((sh) => sh.hoopid === h.hoopid)
        );
        const groupedHoops = groupHoopsByCategory(filteredHoops);
        setAvailableHoops(groupedHoops);

        // Set default hoop if it exists and no hoops are currently selected
        if (defaultGallyHoop && selectedHashtags.length === 0) {
          setSelectedHashtags([defaultGallyHoop.hooptag]);
        }
      }
    }
  }, [selectedGroups, scoops, hoops, defaultGallyHoop]);

  // Handle text input and detect mentions
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);

    // Get cursor position
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    // Check if we're in the middle of typing a mention
    const textBeforeCursor = newContent.substring(0, cursorPos);
    const words = textBeforeCursor.split(/\s/);
    const currentWord = words[words.length - 1];

    // Check if user is logged in before showing the + group mentions
    const userIsLoggedIn = isAuthenticated();

    if (currentWord.startsWith("+")) {
      // Group mention - only show if user is logged in and has groups
      const userIsLoggedIn = isAuthenticated();
      const hasGroups = groups.length > 0;

      if (userIsLoggedIn && hasGroups) {
        const searchText = currentWord.substring(1).toLowerCase();
        setMentionType("+");
        setMentionText(searchText);
        setSelectedMentionIndex(0);
        setShowMentionGuide(true);
        calculateMentionPosition();

        // Filter groups based on search text
        const filteredGroups = groups.filter((group) => {
          return group.name.toLowerCase().includes(searchText);
        });

        setMentionOptions(
          filteredGroups.map((group) => ({
            id: group.groupid.toString(),
            name: group.name,
            type: "group",
          }))
        );
      } else {
        setShowMentionGuide(false);
      }
    } else if (currentWord.startsWith("@")) {
      // Scoop mention - only show if a group is selected or no group is required
      const searchText = currentWord.substring(1).toLowerCase();
      setMentionType("@");
      setMentionText(searchText);
      setSelectedMentionIndex(0);

      // Ensure we have the latest scoops data
      getScoops().then((scoopsData) => {
        // Update the scoops state with fresh data
        setScoops(scoopsData);

        // Filter scoops based on search text
        let filteredScoops;
        if (selectedGroups.length > 0) {
          // Only show Gally Talks scoop
          filteredScoops = scoopsData
            .filter(
              (scoop) =>
                (scoop.scoopname === "Gally Talks" ||
                  scoop.scoopname === "Galli Talks") &&
                scoop.scoopname.toLowerCase().includes(searchText)
            )
            .slice(0, 5);
        } else {
          // Show all scoops
          filteredScoops = scoopsData.filter((scoop) => {
            return scoop.scoopname.toLowerCase().includes(searchText);
          });
        }

        setMentionOptions(
          filteredScoops.map((scoop) => ({
            id: scoop.scoopid.toString(),
            name: scoop.scoopname,
            type: "scoop",
            iconPath: `/scoops/${scoop.scoopname
              .toLowerCase()
              .replace(/\s+/g, "")}.png`,
          }))
        );
      });

      setShowMentionGuide(true);
      calculateMentionPosition();
    } else if (currentWord.startsWith("#")) {
      // Hoop mention - only show if a scoop is selected
      if (selectedCategory) {
        const searchText = currentWord.substring(1).toLowerCase();
        setMentionType("#");
        setMentionText(searchText);
        setSelectedMentionIndex(0);

        // Flatten available hoops and filter based on search text
        const allHoops = hoops
          .filter((hoop) => {
            // Only include hoops that belong to the selected scoop
            const scoop = scoops.find((s) => s.scoopid === selectedCategory);
            if (!scoop || !scoop.hoops) return false;

            const scoopHoops = scoop.hoops.map((h) => h.hoopid);
            return (
              scoopHoops.includes(hoop.hoopid) &&
              hoop.hooptag.toLowerCase().includes(searchText)
            );
          })
          .slice(0, 5); // Limit to 5 results

        setMentionOptions(
          allHoops.map((hoop) => ({
            id: hoop.hoopid.toString(),
            name: hoop.hooptag,
            type: "hoop",
          }))
        );

        setShowMentionGuide(true); // Always show, even if empty
        calculateMentionPosition();
      } else {
        setShowMentionGuide(false);
      }
    } else {
      // Not typing a mention
      setShowMentionGuide(false);
    }
  };

  // Calculate position for mention guide - directly below cursor
  const calculateMentionPosition = () => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const textareaRect = textarea.getBoundingClientRect();

    // Get the container dimensions for boundary checking
    const containerEl = textarea.parentElement;
    const containerRect = containerEl.getBoundingClientRect();

    // Get cursor position
    const cursorPosition = textarea.selectionEnd;

    // Create a temporary element to measure text dimensions
    const tempElement = document.createElement("div");
    tempElement.style.position = "absolute";
    tempElement.style.visibility = "hidden";
    tempElement.style.whiteSpace = "pre-wrap";
    tempElement.style.width = `${textareaRect.width}px`;

    // Copy all relevant styles from the textarea
    const textareaStyles = window.getComputedStyle(textarea);
    const stylesToCopy = [
      "font",
      "fontSize",
      "fontFamily",
      "lineHeight",
      "letterSpacing",
      "textTransform",
      "padding",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "boxSizing",
      "borderWidth",
    ];

    stylesToCopy.forEach((style) => {
      tempElement.style[style] = textareaStyles[style];
    });

    // Get text before cursor and add a span at cursor position
    const textBeforeCursor = textarea.value.substring(0, cursorPosition);
    tempElement.innerHTML =
      textBeforeCursor.replace(/\n/g, "<br>") + '<span id="cursor"></span>';

    // Add to body but position near the textarea for more accurate measurements
    document.body.appendChild(tempElement);
    tempElement.style.top = `${textareaRect.top}px`;
    tempElement.style.left = `${textareaRect.left}px`;

    const cursorSpan = document.getElementById("cursor");

    if (!cursorSpan) {
      document.body.removeChild(tempElement);
      return;
    }

    // Get cursor coordinates
    const cursorRect = cursorSpan.getBoundingClientRect();

    // Clean up
    document.body.removeChild(tempElement);

    // Calculate position relative to textarea
    let top = cursorRect.top - textareaRect.top + textarea.scrollTop;
    let left = cursorRect.left - textareaRect.left + textarea.scrollLeft;

    // Account for line height to position below the current line
    const lineHeight =
      Number.parseFloat(textareaStyles.lineHeight) ||
      Number.parseFloat(textareaStyles.fontSize) * 1.2;
    top += lineHeight;

    // Apply padding adjustments
    const paddingTop = Number.parseFloat(textareaStyles.paddingTop) || 0;
    const paddingLeft = Number.parseFloat(textareaStyles.paddingLeft) || 0;
    top += paddingTop;
    left += paddingLeft;

    // Ensure the dropdown stays within the container bounds
    const dropdownWidth = 256; // w-64 = 16rem = 256px
    const dropdownHeight = 208; // max-h-52 = 13rem = 208px

    // Check right boundary - don't let it extend past container width
    const rightEdge = left + dropdownWidth;
    const containerWidth = containerRect.width;
    if (rightEdge > containerWidth - 20) {
      // 20px buffer
      left = Math.max(10, containerWidth - dropdownWidth - 20);
    }

    // Check bottom boundary
    const bottomEdge = top + dropdownHeight;
    const containerHeight = containerRect.height;
    if (bottomEdge > containerHeight - 20) {
      // Position above the cursor if not enough space below
      top = Math.max(10, top - dropdownHeight - lineHeight);
    }

    // Apply scale factor for zoomed views
    const scaleFactor = 1 / (window.visualViewport?.scale || 1);
    if (scaleFactor !== 1) {
      // Adjust for browser zoom
      top = top * scaleFactor;
      left = left * scaleFactor;
    }

    setMentionPosition({
      top,
      left,
      // Store additional context for responsive positioning
      containerWidth: containerWidth,
      textareaWidth: textareaRect.width,
    });
  };

  // Handle selection from mention guide
  const handleMentionSelect = (option) => {
    // Get text before and after the current mention
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);

    // Find the word with @ or # or + that we need to remove
    const words = textBeforeCursor.split(/\s/);
    const currentWordIndex = words.length - 1;
    const currentWord = words[currentWordIndex];

    // Remove the current mention word completely
    words[currentWordIndex] = "";

    // Join the text back together, trimming any extra spaces
    const newTextBefore = words.join(" ").replace(/\s+/g, " ").trim();
    const newText =
      newTextBefore + (newTextBefore ? " " : "") + textAfterCursor.trimStart();

    setContent(newText);
    setShowMentionGuide(false);

    // Update selected items based on mention type
    if (option.type === "group") {
      // Find the full group object
      const group = groups.find((g) => g.groupid.toString() === option.id);
      if (group) {
        // Only allow one group selection
        setSelectedGroups([group]);

        // If a group is selected, immediately set scoop to Gally Talks
        if (scoops.length > 0) {
          const gallyTalksScoop = scoops.find(
            (scoop) =>
              scoop.scoopname === "Gally Talks" ||
              scoop.scoopname === "Galli Talks"
          );
          if (gallyTalksScoop) {
            setSelectedCategory(gallyTalksScoop.scoopid);

            // Clear existing hoops when changing scoop
            setSelectedHashtags([]);

            // Set default hoop if available
            if (defaultGallyHoop) {
              setSelectedHashtags([defaultGallyHoop.hooptag]);
            }
          } else {
            // Show error if Gally Talks scoop is not found
            errorToast(
              "Gally Talks scoop not found. Please contact support.",
              {
                position: "top-right",
                autoClose: 5000,
              }
            );
          }
        }
      }
    } else if (option.type === "scoop") {
      // Find the full scoop object
      const scoop = scoops.find((s) => s.scoopid.toString() === option.id);
      if (scoop) {
        // Clear existing hoops when changing scoop
        setSelectedHashtags([]);
        setSelectedGroups([]); // Clear groups when changing scoop
        setSelectedCategory(scoop.scoopid);
      }
    } else if (option.type === "hoop") {
      // Only allow hoop selection if a scoop is selected
      if (!selectedCategory) {
        warningToast("Please select a scoop first", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Find the full hoop object
      const hoop = hoops.find((h) => h.hoopid.toString() === option.id);
      if (hoop && !selectedHashtags.includes(hoop.hooptag)) {
        setSelectedHashtags([...selectedHashtags, hoop.hooptag]);
      }
    }

    // Set focus back to textarea
    if (textareaRef.current) {
      textareaRef.current.focus();

      // Set cursor position after where the mention was
      const newCursorPosition = newTextBefore.length;
      setTimeout(() => {
        textareaRef.current.selectionStart = newCursorPosition;
        textareaRef.current.selectionEnd = newCursorPosition;
      }, 0);
    }
  };

  const toggleHashtag = (hoopTag) => {
    if (selectedHashtags.includes(hoopTag)) {
      setSelectedHashtags(selectedHashtags.filter((t) => t !== hoopTag));
    } else {
      setSelectedHashtags([...selectedHashtags, hoopTag]);
    }
  };

  const toggleGroup = (group) => {
    if (selectedGroups.some((g) => g.groupid === group.groupid)) {
      setSelectedGroups(
        selectedGroups.filter((g) => g.groupid !== group.groupid)
      );
      // If we're removing the group, we don't need to auto-set the scoop
    } else {
      // Only allow one group selection
      setSelectedGroups([group]);

      // If a group is selected, immediately set scoop to Gally Talks
      if (scoops.length > 0) {
        const gallyTalksScoop = scoops.find(
          (scoop) =>
            scoop.scoopname === "Gally Talks" ||
            scoop.scoopname === "Galli Talks"
        );
        if (gallyTalksScoop) {
          setSelectedCategory(gallyTalksScoop.scoopid);

          // Clear existing hoops when changing scoop
          setSelectedHashtags([]);

          // Set default hoop if available
          if (defaultGallyHoop) {
            setSelectedHashtags([defaultGallyHoop.hooptag]);
          }
        } else {
          // Show error if Gally Talks scoop is not found
          errorToast("Gally Talks scoop not found. Please contact support.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      }
    }
  };

  const openImagePicker = (mode) => {
    if (images.length >= MAX_IMAGES) {
      setShowMaxImagesWarning(true);
      return;
    }
    setImagePickerMode(mode);
    setIsImagePickerOpen(true);
  };

  const handleImageSelected = (imageData) => {
    // Check if we've reached the maximum number of images
    if (images.length >= MAX_IMAGES) {
      setShowMaxImagesWarning(true);
      setIsImagePickerOpen(false);
      return;
    }

    // Handle both URL strings and File objects
    if (typeof imageData === "string") {
      // If it's a URL string
      setImages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          url: imageData,
          type: "url",
        },
      ]);
    } else if (imageData instanceof File) {
      // If it's a File object
      const imageUrl = URL.createObjectURL(imageData);
      setImages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          url: imageUrl,
          file: imageData,
          type: "file",
        },
      ]);
    } else if (imageData && imageData.url) {
      // If it's an object with a url property
      setImages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          ...imageData,
          type: imageData.file ? "file" : "url",
        },
      ]);
    }

    setIsImagePickerOpen(false);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      // Check if adding these files would exceed the limit
      if (images.length + e.target.files.length > MAX_IMAGES) {
        setShowMaxImagesWarning(true);
        // Only add files up to the maximum
        const remainingSlots = MAX_IMAGES - images.length;
        if (remainingSlots <= 0) {
          return;
        }

        // Only process the first remainingSlots files
        Array.from(e.target.files)
          .slice(0, remainingSlots)
          .forEach((file) => {
            if (file.type.startsWith("image/")) {
              const imageUrl = URL.createObjectURL(file);
              setImages((prev) => [
                ...prev,
                {
                  id: crypto.randomUUID(),
                  url: imageUrl,
                  file: file,
                  type: "file",
                },
              ]);
            }
          });
      } else {
        // Add all files if we're under the limit
        Array.from(e.target.files).forEach((file) => {
          if (file.type.startsWith("image/")) {
            const imageUrl = URL.createObjectURL(file);
            setImages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                url: imageUrl,
                file: file,
                type: "file",
              },
            ]);
          }
        });
      }
    }
    // Reset the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (imageId) => {
    setImages((prev) => {
      const imageToRemove = prev.find((img) => img.id === imageId);

      // Revoke object URL if it was created with URL.createObjectURL
      if (imageToRemove && imageToRemove.type === "file" && imageToRemove.url) {
        URL.revokeObjectURL(imageToRemove.url);
      }

      return prev.filter((img) => img.id !== imageId);
    });
  };

  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    setStep("content");
    // Reset hashtags when changing category
    setSelectedHashtags([]);
  };

  const handlePost = async () => {
    const access_token = localStorage.getItem("access_token");
    if (!access_token) {
      successToast("Please login to post", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      navigate("/signin");
      return;
    }

    // Helper function for error toasts
    const showErrorToast = (message) => {
      errorToast(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    };

    try {
      // Validation checks
      if (!content.trim()) {
        showErrorToast("Please enter some content");
        return;
      }

      if (!selectedCategory) {
        showErrorToast("Please select a category");
        return;
      }

      // Extract file objects from images for submission
      const imageFiles = images
        .filter((img) => img.file instanceof File)
        .map((img) => img.file);

      // Get hoopids from selected hashtags
      const hoopIds = [];
      for (const tag of selectedHashtags) {
        // Find the hoop object that matches this tag
        const hoop = hoops.find((h) => h.hooptag === tag);
        if (hoop) {
          hoopIds.push(hoop.hoopid);
        }
      }

      const isGroupPost = selectedGroups.length > 0;

      if (isGroupPost) {
        // Create group post
        const groupId = selectedGroups[0].groupid;
        const formData = new FormData();

        formData.append("content", content);
        formData.append("scoopId", selectedCategory);
        formData.append("geoHash", geohash);
        formData.append("hoopIds", hoopIds.join(","));

        // Add images to FormData
        imageFiles.forEach((file) => {
          formData.append("photopath", file);
        });

        await handleAddGroupPost(formData, groupId);

        // Show success message
        successToast(
          `Post successfully added to ${selectedGroups[0].name} group!`,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
          }
        );
      } else {
        // Create regular post
        const formData = new FormData();

        formData.append("contentinfo", content);
        formData.append("scoopid", selectedCategory);
        formData.append("geohash", geohash);
        formData.append("hoopids", hoopIds.join(","));

        // Add images to FormData
        imageFiles.forEach((file) => {
          formData.append("photopath", file);
        });

        await postScoops(formData);

        // Find the scoop name for the success message
        const scoop = scoops.find((s) => s.scoopid === selectedCategory);
        const scoopName = scoop ? scoop.scoopname : "selected category";

        // Show success message
        successToast(`Scoop posted to ${scoopName} successfully!`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      }

      // Clean up object URLs
      images.forEach((img) => {
        if (img.type === "file" && img.url) {
          URL.revokeObjectURL(img.url);
        }
      });

      // Close the form or reset it
      if (onPost) {
        onPost();
      } else {
        // Reset form
        setContent("");
        setSelectedCategory(null);
        setSelectedHashtags([]);
        setSelectedGroups([]);
        setImages([]);
        setStep("category");
      }
    } catch (error) {
      console.error("Post error:", error);

      // Extract error message from various possible formats
      const errorMessage =
        error.response?.data?.message ||
        error.data?.message ||
        error.message ||
        "An error occurred";

      errorToast(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.type === "file" && img.url) {
          URL.revokeObjectURL(img.url);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (showGroupHint) {
      const timer = setTimeout(() => {
        setShowGroupHint(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showGroupHint]);

  // Add CSS for mention guide animations
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .mention-guide-animation {
      animation: fadeIn 0.2s ease-out;
    }
    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.7); }
      70% { box-shadow: 0 0 0 10px rgba(72, 187, 120, 0); }
      100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
    }
    .pulse-animation {
      animation: pulse 2s infinite;
    }
  `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle keyboard navigation in mention guide
  const handleKeyDown = (e) => {
    if (showMentionGuide && mentionOptions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex((prevIndex) =>
          prevIndex < mentionOptions.length - 1 ? prevIndex + 1 : prevIndex
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : 0
        );
      } else if (e.key === "Enter" && mentionOptions.length > 0) {
        e.preventDefault();
        handleMentionSelect(mentionOptions[selectedMentionIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionGuide(false);
      }
    }
  };

  // Add click outside handler to close mention guide
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionGuideRef.current &&
        !mentionGuideRef.current.contains(event.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target)
      ) {
        setShowMentionGuide(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Recalculate position when content changes
  useEffect(() => {
    if (showMentionGuide) {
      calculateMentionPosition();
    }
  }, [content, showMentionGuide]);

  const getStepTitle = () => {
    switch (step) {
      case "category":
        return "Create Scoop";
      case "content":
        return "Create Scoop";
      case "hashtags":
        return "Add Hashtags";
      case "groups":
        return "Select Group";
      default:
        return "Create Scoop";
    }
  };

  // MentionItem component for the mention guide
  const MentionItem = ({ option, onClick, isSelected }) => {
    let icon;
    let bgColor;

    if (option.type === "scoop") {
      icon = (
        <img
          src={option.iconPath || "/placeholder.svg"}
          alt={option.name}
          width={16}
          height={16}
          className="object-contain"
        />
      );
      bgColor = "bg-amber-100";
    } else if (option.type === "hoop") {
      icon = <Hash size={14} />;
      bgColor = "bg-fuchsia-100";
    } else if (option.type === "group") {
      icon = <Plus size={14} />;
      bgColor = "bg-green-100";
    }

    // Highlight matching text
    const highlightedName = () => {
      if (!mentionText) return option.name;

      const index = option.name
        .toLowerCase()
        .indexOf(mentionText.toLowerCase());
      if (index === -1) return option.name;

      const before = option.name.substring(0, index);
      const match = option.name.substring(index, index + mentionText.length);
      const after = option.name.substring(index + mentionText.length);

      return (
        <>
          {before}
          <span className="font-bold bg-yellow-100">{match}</span>
          {after}
        </>
      );
    };

    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-gray-200 ${
          isSelected ? "bg-gray-100" : "hover:bg-gray-100"
        }`}
        onClick={() => onClick(option)}
      >
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center ${bgColor}`}
        >
          {icon}
        </div>
        <div className="font-medium">{highlightedName()}</div>
      </div>
    );
  };

  const renderCategoryStep = () => {
    if (loading) {
      return (
        <div className="p-4 flex flex-col items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-gray-500">Loading categories...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4 flex flex-col items-center justify-center h-64">
          <p className="text-red-500 mb-4">{error}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      );
    }

    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-4">Select a category</h2>
        <div className="grid gap-3">
          {scoops.map((scoop) => (
            <button
              key={scoop.scoopid}
              className={classNames(
                "flex items-center gap-3 p-3 rounded-md border cursor-pointer",
                scoop.scoopid === selectedCategory
                  ? "border-primary"
                  : "border-gray-200"
              )}
              onClick={() => handleSelectCategory(scoop.scoopid)}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">
                <span className="text-xl">
                  <img
                    src={`/scoops/${scoop.scoopname
                      .toLowerCase()
                      .replace(/\s+/g, "")}.png`}
                    alt={`${scoop.scoopname} icon`}
                    width={20}
                    height={20}
                    className="object-contain"
                  />
                </span>
              </div>
              <span className="font-medium">{scoop.scoopname}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderContentStep = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 flex-1">
        <div className="mb-4 relative">
          <textarea
            ref={textareaRef}
            placeholder="What's on your mind? Pick a + Group or start a @ Scoop, add the # Hoop — your voice, your vibe"
            className="w-full p-2 min-h-[150px] border-none outline-none resize-none"
            value={content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
            disabled={isProcessing}
          />

          {/* Mention guide dropdown */}
          {showMentionGuide && (
            <div
              ref={mentionGuideRef}
              className="bg-white border rounded-md shadow-lg overflow-y-auto max-h-52 w-64 mention-guide-animation"
              style={{
                position: "absolute",
                zIndex: 99999,
                maxWidth: "calc(100% - 1rem)",
                top: mentionPosition.top,
                left: mentionPosition.left,
                display: showMentionGuide ? "block" : "none",
              }}
            >
              <div className="sticky top-0 p-2 bg-gray-100 border-b flex items-center gap-2 z-20">
                <Search size={14} className="text-gray-600" />
                <span className="text-sm font-medium">
                  {mentionType === "+"
                    ? `👥 Select a group${
                        mentionText ? `: ${mentionText}` : ""
                      }`
                    : mentionType === "@"
                    ? `🔍 Select a scoop${
                        mentionText ? `: ${mentionText}` : ""
                      }`
                    : `🏷️ Select a hoop${
                        mentionText ? `: ${mentionText}` : ""
                      }`}
                </span>
              </div>

              {mentionOptions.length > 0 ? (
                <div className="py-1">
                  {mentionOptions.map((option, index) => (
                    <MentionItem
                      key={`${option.type}-${option.id}`}
                      option={option}
                      onClick={handleMentionSelect}
                      isSelected={index === selectedMentionIndex}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-3 text-center text-gray-500 italic">
                  No matches found
                  <div className="text-xl mt-1">🔍</div>
                </div>
              )}

              <div className="p-2 bg-gray-50 border-t text-xs text-gray-500 flex justify-between">
                <span>↑↓ to navigate</span>
                <span>↵ to select</span>
                <span>Esc to close</span>
              </div>
            </div>
          )}
        </div>

        {/* Max images warning message */}
        {showMaxImagesWarning && (
          <div className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
            You can only upload a maximum of {MAX_IMAGES} images.
          </div>
        )}

        {/* Horizontal image layout */}
        {images.length > 0 && (
          <div className="mb-4">
            <div className="flex gap-2">
              {images.map((image) => (
                <div key={image.id} className="flex-1">
                  <ImagePreview
                    src={image.url || "/placeholder.svg"}
                    onRemove={() => !isProcessing && removeImage(image.id)}
                  />
                </div>
              ))}

              {/* Empty placeholder to maintain grid when only one image */}
              {images.length === 1 && <div className="flex-1"></div>}
            </div>
            <p className="text-xs text-gray-500 mt-1 text-right">
              {images.length}/{MAX_IMAGES} images
            </p>
          </div>
        )}

        {/* Selected items pills section */}
        <div className="flex flex-wrap gap-2 mb-4">
          {/* Selected scoop - always first */}
          {selectedCategory && (
            <span
              className="px-3 py-1 rounded-full text-sm font-medium bg-black text-white flex items-center gap-1"
              onClick={() => !isProcessing && setStep("category")}
            >
              {scoops.find((s) => s.scoopid === selectedCategory)?.scoopname ||
                "Category"}
              <X
                size={14}
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isProcessing) setSelectedCategory(null);
                }}
              />
            </span>
          )}

          {/* Group pill */}
          {selectedGroups.map((group) => (
            <span
              key={group.groupid}
              className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-gray-700 flex items-center gap-1"
              onClick={() => !isProcessing && toggleGroup(group)}
            >
              {group.name}
              <X
                size={14}
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isProcessing) setSelectedGroups([]);
                }}
              />
            </span>
          ))}

          {/* Hashtags */}
          {selectedHashtags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 rounded-full text-sm font-medium bg-fuchsia-100 text-gray-700 flex items-center gap-1"
              onClick={() => !isProcessing && toggleHashtag(tag)}
            >
              {tag}
              <X
                size={14}
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isProcessing) toggleHashtag(tag);
                }}
              />
            </span>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => !isProcessing && openImagePicker("camera")}
              className={classNames(
                "transition-colors",
                isProcessing || images.length >= MAX_IMAGES
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-500 hover:text-gray-800"
              )}
              disabled={isProcessing || images.length >= MAX_IMAGES}
            >
              <Camera size={24} />
            </button>
            <label
              className={classNames(
                "cursor-pointer",
                isProcessing || images.length >= MAX_IMAGES
                  ? "cursor-not-allowed"
                  : ""
              )}
            >
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                multiple
                onChange={handleFileInputChange}
                disabled={isProcessing || images.length >= MAX_IMAGES}
              />
              <ImageIcon
                size={24}
                className={classNames(
                  "transition-colors",
                  isProcessing || images.length >= MAX_IMAGES
                    ? "text-gray-300"
                    : "text-gray-500 hover:text-gray-800"
                )}
              />
            </label>
            {/* Group button - always show if authenticated */}
            {isAuthenticated() && (
              <div className="relative">
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      setShowGroupHint(false);
                      if (!isProcessing) {
                        // If no groups, fetch them again in case they were added recently
                        if (groups.length === 0) {
                          fetchGroups(true).then((fetchedGroups) => {
                            if (fetchedGroups.length > 0) {
                              setStep("groups");
                            }
                          });
                        } else {
                          setStep("groups");
                        }
                      }
                    }}
                    className={classNames(
                      "relative transition-all transform hover:scale-110",
                      isProcessing
                        ? "text-gray-300 cursor-not-allowed"
                        : groups.length > 0
                        ? "text-white bg-green-500 hover:bg-green-600 rounded-full p-1.5 pulse-animation"
                        : "text-white bg-gray-400 hover:bg-gray-500 rounded-full p-1.5"
                    )}
                    disabled={isProcessing}
                  >
                    <Plus size={20} />
                    {selectedGroups.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {selectedGroups.length}
                      </span>
                    )}
                  </button>

                  {/* Refresh button for groups */}
                  <button
                    onClick={() => {
                      if (!isProcessing && !groupsLoading) {
                        fetchGroups(true);
                      }
                    }}
                    className={classNames(
                      "ml-1 p-1 rounded-full",
                      isProcessing || groupsLoading
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                    )}
                    disabled={isProcessing || groupsLoading}
                    title="Refresh groups"
                  >
                    <RefreshCw
                      size={16}
                      className={groupsLoading ? "animate-spin" : ""}
                    />
                  </button>
                </div>

                {showGroupHint && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-32 bg-black bg-opacity-75 text-white text-xs rounded p-2 pointer-events-none mention-guide-animation">
                    {groups.length > 0
                      ? "Select a group to post to"
                      : "No groups joined yet"}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black border-opacity-75"></div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => !isProcessing && setStep("hashtags")}
              className={classNames(
                "transition-colors",
                isProcessing
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-500 hover:text-gray-800"
              )}
              disabled={isProcessing}
            >
              <Hash size={24} />
            </button>
          </div>

          {(content || images.length > 0 || selectedHashtags.length > 0) && (
            <Button
              variant="primary"
              className="text-xs px-3 py-1.5 bg-[#7B189F] text-white rounded-md font-medium shadow hover:bg-[#8F1DB8] transition-all duration-200 whitespace-nowrap"
              onClick={handlePost}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Posting...
                </>
              ) : (
                "Post"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const renderHashtagsStep = () => {
    const scoop = scoops.find((s) => s.scoopid === selectedCategory);
    const categoryHoops = scoop?.hoops || [];

    if (categoryHoops.length === 0) {
      return (
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-4">No hashtags available</h2>
          <p className="text-gray-500 mb-6">
            This category doesn't have any hashtags.
          </p>
          <Button
            variant="primary"
            className="w-full"
            onClick={() => setStep("content")}
            disabled={isProcessing}
          >
            Back to Content
          </Button>
        </div>
      );
    }

    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-4">Select hashtags</h2>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {categoryHoops.map((hoop) => (
            <Hashtag
              key={hoop.hoopid}
              tag={hoop.hooptag}
              selected={selectedHashtags.includes(hoop.hooptag)}
              onClick={() => !isProcessing && toggleHashtag(hoop.hooptag)}
            />
          ))}
        </div>

        <Button
          variant="primary"
          className="w-full"
          onClick={() => setStep("content")}
          disabled={isProcessing}
        >
          Done
        </Button>
      </div>
    );
  };

  // Add a useEffect to fetch groups when the step changes to "groups"
  useEffect(() => {
    if (step === "groups") {
      fetchGroups();
    }
  }, [step]);

  // Add a search functionality to the groups step
  const [groupSearchQuery, setGroupSearchQuery] = useState("");

  // Update the renderGroupsStep function to include search functionality
  const renderGroupsStep = () => {
    if (groupsLoading) {
      return (
        <div className="p-4 flex flex-col items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-gray-500">Loading groups...</p>
        </div>
      );
    }

    if (groupsError) {
      return (
        <div className="p-4 flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-lg font-semibold mb-2">Error loading groups</h2>
          <p className="text-gray-500 mb-6 text-center">{groupsError}</p>
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setStep("content")}
              disabled={isProcessing}
            >
              Back
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => fetchGroups(true)}
              disabled={isProcessing || groupsLoading}
            >
              {groupsLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Retrying...
                </>
              ) : (
                "Retry"
              )}
            </Button>
          </div>
        </div>
      );
    }

    if (!groups || groups.length === 0) {
      return (
        <div className="p-4 flex flex-col items-center justify-center h-64">
          <div className="bg-gray-100 rounded-full p-4 mb-4">
            <Plus size={32} className="text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">No groups available</h2>
          <p className="text-gray-500 mb-6 text-center">
            You haven't joined any groups yet.
          </p>
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setStep("content")}
              disabled={isProcessing}
            >
              Back
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => fetchGroups(true)}
              disabled={isProcessing || groupsLoading}
            >
              {groupsLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Refreshing...
                </>
              ) : (
                "Refresh"
              )}
            </Button>
          </div>
        </div>
      );
    }

    // Filter groups based on search query
    const filteredGroups = groupSearchQuery
      ? groups.filter(
          (group) =>
            group.name.toLowerCase().includes(groupSearchQuery.toLowerCase()) ||
            (group.description &&
              group.description
                .toLowerCase()
                .includes(groupSearchQuery.toLowerCase()))
        )
      : groups;

    return (
      <div className="p-4">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Select a group</h2>
            <div className="text-xs text-gray-500">
              {groupsLastFetched
                ? `Updated ${new Date(groupsLastFetched).toLocaleTimeString()}`
                : ""}
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Choose a group to share your post with. Your post will only be
            visible to group members.
          </p>

          {/* Search input */}
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Search groups..."
              value={groupSearchQuery}
              onChange={(e) => setGroupSearchQuery(e.target.value)}
            />
            {groupSearchQuery && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setGroupSearchQuery("")}
              >
                <X size={16} className="text-gray-400 hover:text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {filteredGroups.length === 0 ? (
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
              <Search size={24} className="text-gray-400" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No groups found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Try a different search term or clear your search.
            </p>
            <div className="mt-6">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                onClick={() => setGroupSearchQuery("")}
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-3 mb-6">
            {filteredGroups.map((group) => {
              const isSelected = selectedGroups.some(
                (g) => g.groupid === group.groupid
              );
              return (
                <button
                  key={group.groupid}
                  className={classNames(
                    "flex items-center gap-3 p-3 rounded-lg border transition-all",
                    isSelected
                      ? "border-green-500 bg-green-50 shadow-md"
                      : "border-gray-200 hover:border-green-300 hover:bg-green-50/50"
                  )}
                  onClick={() => !isProcessing && toggleGroup(group)}
                >
                  <div
                    className={classNames(
                      "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
                      isSelected
                        ? "bg-green-500 text-white"
                        : "bg-green-100 text-green-800"
                    )}
                  >
                    <Plus size={24} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{group.name}</div>
                    <div className="text-xs text-gray-500 line-clamp-1">
                      {group.description || "No description"}
                    </div>
                  </div>
                  <div
                    className={classNames(
                      "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all",
                      isSelected
                        ? "border-green-500 bg-green-500"
                        : "border-gray-300"
                    )}
                  >
                    {isSelected && <Check className="text-white" size={14} />}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setStep("content")}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1 bg-green-500 hover:bg-green-600"
            onClick={() => setStep("content")}
            disabled={isProcessing}
          >
            Done
          </Button>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case "category":
        return renderCategoryStep();
      case "content":
        return renderContentStep();
      case "hashtags":
        return renderHashtagsStep();
      case "groups":
        return renderGroupsStep();
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <MobileHeader
        title={getStepTitle()}
        onBack={
          step === "category"
            ? onClose
            : () => setStep(step === "content" ? "category" : "content")
        }
        disabled={isProcessing}
      />

      <div className="flex-1 overflow-auto">
        {isProcessing && step !== "content" && (
          <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              <p className="text-sm text-gray-600">Posting your scoop...</p>
            </div>
          </div>
        )}
        {renderStepContent()}
      </div>

      <ImagePickerModal
        isOpen={isImagePickerOpen && !isProcessing}
        onClose={() => setIsImagePickerOpen(false)}
        onImageSelected={handleImageSelected}
        mode={imagePickerMode}
      />
    </div>
  );
}

export default MobileCreateScoop;
